import {Box, Divider, Drawer, IconButton, Stack, Typography} from '@mui/material';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {map, Observable, of} from 'rxjs';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import SelectComponent, {SelectItem} from '@components/inputs/select/Select.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';

import AsyncMultiSelectWithSearchComponent
  from '@components/inputs/asyncMultiSelect/AsyncMultiSelectWithSearch.component';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import {Sizes} from '@constants/sizes.constants';
import {AddWhite, Close, Delete} from '@icons/index';
import {LoadingButton} from '@mui/lab';
import {FacturationRuleType} from '@store/clients/details/client-details.enums';
import {FacturationMode} from '@store/clients/details/facturationMode/facturation-mode.model';
import {clientsUnitsService} from '@store/clients/units';
import {serviceKindsService} from '@store/settings/serviceKinds';
import {wasteFamiliesService} from '@store/settings/wasteFamilies';
import {useParams} from 'react-router-dom';

interface ManageBillingMethodDrawerProps {
  facturationMode: FacturationMode;
  handleAdd: (updatedData: FacturationMode) => void;
  handleClose: () => void;
  readOnly?: boolean;
  loading?: boolean;
  fromOrganisationUnit?: boolean;
}

const ManageBillingMethodDrawer = (props: ManageBillingMethodDrawerProps) => {
  const {
    facturationMode: facturationModeProps,
    handleAdd,
    handleClose,
    readOnly,
    loading,
    fromOrganisationUnit,
  } = props;

  const {clientId} = useParams();
  const {t} = useTranslation();

  const [facturationMode, setFacturationMode] = useState<FacturationMode>(facturationModeProps || {});

  const updateFacturationMode = (name: string) => (value: any) =>
    setFacturationMode((state) => ({
      ...state,
      [name]: value,
    }));
  const updateFacturationModeGroups = (name: string, groupIndex: number) => (value: any) => {
    const groups = [...(facturationMode.facturationGroups || [])];
    groups[groupIndex] = {...groups[groupIndex], [name]: value};
    updateFacturationMode('facturationGroups')(groups);
  };

  const updateFacturationModeGroupsRules = (name: string, groupIndex: number, ruleIndex: number) => (value: any) => {
    const rules = [...(facturationMode.facturationGroups[groupIndex].facturationRules || [])];
    rules[ruleIndex] = {...rules[ruleIndex], [name]: value};
    updateFacturationModeGroups('facturationRules', groupIndex)(rules);
  };

  const errors = !readOnly
    ? {
      name: getFieldError(facturationMode.name, FieldValidationType.REQUIRED_STRING),
      email: getFieldError(facturationMode.email, FieldValidationType.REQUIRED_STRING),
      facturationGroups:
        getFieldError(facturationMode.facturationGroups, FieldValidationType.REQUIRED_ARRAY) ||
        (facturationMode.facturationGroups || []).map((group) => ({
          name: getFieldError(group.name, FieldValidationType.REQUIRED_STRING),
          priority: getFieldError(group.priority, FieldValidationType.REQUIRED_NUMBER),
          facturationRules:
            getFieldError(group.facturationRules, FieldValidationType.REQUIRED_ARRAY) ||
            (group.facturationRules || []).map((rule) => ({
              type: getFieldError(rule.type, FieldValidationType.REQUIRED_STRING),
              priority: getFieldError(rule.priority, FieldValidationType.REQUIRED_NUMBER),
            })),
        })),
    }
    : {};

  const searchOnType = (search: string | undefined, type: FacturationRuleType): Observable<SelectItem[]> => {
    switch (type) {
      case FacturationRuleType.INVOICE_ADDRESS:
        return of([]);
      case FacturationRuleType.CLIENT_UNIT_DISTINCT:
      case FacturationRuleType.CLIENT_UNIT_TOGETHER:
        return clientsUnitsService
          .searchClientsUnits(search, clientId ? [clientId] : undefined)
          .pipe(map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id}))));
      case FacturationRuleType.SERVICE_KIND_DISTINCT:
        return serviceKindsService
          .searchServiceKinds(search)
          .pipe(map((options) => options.data.map((o) => ({label: o.kind || '-', value: o.id}))));
      case FacturationRuleType.WASTE_FAMILY_DISTINCT:
        return wasteFamiliesService.getWasteFamilies().pipe(
          map((options) =>
            options.data.map((o) => ({
              label: o.name || '-',
              value: o.id,
            }))
          )
        );
      case FacturationRuleType.OTHER_CLIENT_UNIT:
        return clientsUnitsService
          .searchClientsUnits(search)
          .pipe(map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id}))));
      case FacturationRuleType.SERVICE_SCHEDULED_OR_NOT:
        return of([]);
    }
  };

  return (
    <Drawer anchor="right" open onClose={handleClose}>
      <Stack flex={1} overflow="hidden">
        <Stack mr="10px">
          <Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb="12px" mr="-8px">
              <Typography fontSize={18} fontWeight="700">
                {t('unitDetails.billingMethod.title')}
              </Typography>
              <IconButton onClick={handleClose}>
                <Close/>
              </IconButton>
            </Stack>
            <Divider variant="fullWidth" orientation="horizontal"/>
          </Stack>
          <Stack py="20px" spacing={2}>
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('unitDetails.billingMethod.name')}
              placeholder={t('global.writeSomething')}
              handleChange={updateFacturationMode('name')}
              value={facturationMode.name || ''}
              error={errors.name}
              readOnly={readOnly}
              required
            />
            <CheckboxComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('unitDetails.billingMethod.sendInvoiceByMail')}
              handleChange={updateFacturationMode('sendInvoiceByMail')}
              value={facturationMode.sendInvoiceByMail}
              readOnly={readOnly}
            />
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('unitDetails.billingMethod.email')}
              placeholder={t('global.writeSomething')}
              handleChange={updateFacturationMode('email')}
              value={facturationMode.email || ''}
              error={errors.email}
              readOnly={readOnly}
              required
            />
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal"/>
        </Stack>
        <Stack pt="20px" flex={1} spacing={1} overflow="hidden">
          <Stack direction="row" alignItems="center" justifyContent="space-between" mr="10px">
            <Typography fontSize={18} fontWeight="700">
              {t('unitDetails.billingMethod.groups.title')}
            </Typography>
            {!readOnly && (
              <IconButton
                aria-label="small-square-text"
                centerRipple={false}
                onClick={() =>
                  updateFacturationMode('facturationGroups')(
                    (facturationMode.facturationGroups || []).concat({
                      facturationRules: [],
                      isCreditNote: false,
                    })
                  )
                }
              >
                <AddWhite/>
              </IconButton>
            )}
          </Stack>
          {facturationMode.facturationGroups.length !== 0 && (
            <Stack spacing={3} overflow="auto" className="scrollable" py="20px" pr="10px">
              {facturationMode.facturationGroups.map((facturationGroup, groupIndex) => (
                <Stack spacing={1} key={`group-${groupIndex}`} mr="-5px">
                  <Stack spacing={2} pr="5px">
                    <TextFieldComponent
                      labelWidth={Sizes.drawerInputLabelWidth}
                      label={t('unitDetails.billingMethod.groups.name')}
                      placeholder={t('global.writeSomething')}
                      handleChange={updateFacturationModeGroups('name', groupIndex)}
                      value={facturationGroup.name || ''}
                      error={errors?.facturationGroups[groupIndex]?.name}
                      readOnly={readOnly}
                      required
                    />
                    <NumberTextFieldComponent
                      labelWidth={Sizes.drawerInputLabelWidth}
                      label={t('unitDetails.billingMethod.groups.priority')}
                      placeholder={t('global.writeSomething')}
                      handleChange={updateFacturationModeGroups('priority', groupIndex)}
                      value={facturationGroup.priority ?? 0}
                      error={errors?.facturationGroups[groupIndex]?.priority}
                      readOnly={readOnly}
                      required
                    />
                    <CheckboxComponent
                      labelWidth={Sizes.drawerInputLabelWidth}
                      label={t('unitDetails.billingMethod.groups.negative')}
                      handleChange={updateFacturationModeGroups('isCreditNote', groupIndex)}
                      value={facturationGroup.isCreditNote}
                      readOnly={readOnly}
                    />
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mr="10px" pl="30px">
                      <Typography fontSize={18} fontWeight="700">
                        {t('unitDetails.billingMethod.groups.rules.title')}
                      </Typography>
                      {!readOnly && (
                        <IconButton
                          aria-label="small-square-text"
                          centerRipple={false}
                          onClick={() =>
                            updateFacturationModeGroups(
                              'facturationRules',
                              groupIndex
                            )((facturationGroup.facturationRules || []).concat({value: []}))
                          }
                        >
                          <AddWhite/>
                        </IconButton>
                      )}
                    </Stack>
                    {facturationGroup.facturationRules.map((facturationRule, ruleIndex) => (
                      <Stack spacing={1} key={`rule-${groupIndex}-${ruleIndex}`} mr="-5px" pl="30px">
                        <Stack spacing={2} pr="5px">
                          <SelectComponent
                            labelWidth={Sizes.drawerInputLabelWidth}
                            label={t('unitDetails.billingMethod.groups.rules.type')}
                            placeholder={t('global.writeSomething')}
                            handleChange={updateFacturationModeGroupsRules('type', groupIndex, ruleIndex)}
                            value={facturationRule.type ?? ''}
                            items={FacturationRuleType.selectItems(fromOrganisationUnit)}
                            error={errors?.facturationGroups?.[groupIndex]?.facturationRules?.[ruleIndex]?.type}
                            readOnly={readOnly}
                            required
                          />
                          <NumberTextFieldComponent
                            labelWidth={Sizes.drawerInputLabelWidth}
                            label={t('unitDetails.billingMethod.groups.rules.priority')}
                            placeholder={t('global.writeSomething')}
                            handleChange={updateFacturationModeGroupsRules('priority', groupIndex, ruleIndex)}
                            value={facturationRule.priority ?? 0}
                            error={errors?.facturationGroups?.[groupIndex]?.facturationRules?.[ruleIndex]?.priority}
                            readOnly={readOnly}
                            required
                          />
                          {facturationRule.type &&
                            facturationRule.type !== FacturationRuleType.SERVICE_SCHEDULED_OR_NOT && (
                              <AsyncMultiSelectWithSearchComponent
                                labelWidth={Sizes.drawerInputLabelWidth}
                                label={t('unitDetails.billingMethod.groups.rules.value')}
                                placeholder={t('global.selectOptions')}
                                handleChange={(items) =>
                                  updateFacturationModeGroupsRules(
                                    'value',
                                    groupIndex,
                                    ruleIndex
                                  )(
                                    items.map((it) => ({
                                      label: it.label,
                                      id: it.value,
                                    }))
                                  )
                                }
                                values={facturationRule.value.map((it) => ({
                                  label: it.label,
                                  value: it.id,
                                }))}
                                getOptions={(search) => searchOnType(search, facturationRule.type!)}
                              />
                            )}
                        </Stack>
                        {!readOnly && (
                          <Box alignSelf="flex-end">
                            <IconButton
                              onClick={() => {
                                updateFacturationModeGroups(
                                  'facturationRules',
                                  groupIndex
                                )((facturationGroup.facturationRules || []).filter((_, idx) => idx !== ruleIndex));
                              }}
                            >
                              <Delete/>
                            </IconButton>
                          </Box>
                        )}
                        <Divider/>
                      </Stack>
                    ))}
                  </Stack>
                  {!readOnly && (
                    <Box alignSelf="flex-end">
                      <IconButton
                        onClick={() => {
                          updateFacturationMode('facturationGroups')(
                            (facturationMode.facturationGroups || []).filter((_, idx) => idx !== groupIndex)
                          );
                        }}
                      >
                        <Delete/>
                      </IconButton>
                    </Box>
                  )}
                  <Divider/>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
        {!readOnly && (
          <Box alignSelf="flex-end" justifySelf="flex-end" my="20px" mr="10px">
            <LoadingButton
              variant="contained"
              color="primary"
              loading={loading}
              disabled={checkIfErrors(errors)}
              onClick={() => handleAdd(facturationMode)}
            >
              {t(`global.${facturationModeProps ? 'modify' : 'add'}`)}
            </LoadingButton>
          </Box>
        )}
      </Stack>
    </Drawer>
  );
};

export default ManageBillingMethodDrawer;
